import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProgressProvider } from './contexts/ProgressContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import DashboardPage from './pages/DashboardPage';
import SectionPage from './pages/SectionPage';
import LessonPage from './pages/LessonPage';
import AdminPage from './pages/AdminPage';

// Router component that handles OAuth callback detection
function AppRouter() {
    const location = useLocation();
    
    // CRITICAL: Check URL fragment synchronously during render for session_id
    // This prevents race conditions by processing OAuth callback FIRST
    // before ProtectedRoute runs its auth check
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Pending approval page - for authenticated but not active users */}
            <Route
                path="/pending"
                element={
                    <ProtectedRoute allowPending>
                        <PendingApprovalPage />
                    </ProtectedRoute>
                }
            />

            {/* Protected routes - require active status */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/section/:sectionId"
                element={
                    <ProtectedRoute>
                        <SectionPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lesson/:lessonId"
                element={
                    <ProtectedRoute>
                        <LessonPage />
                    </ProtectedRoute>
                }
            />
            
            {/* Admin route */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute requireAdmin>
                        <AdminPage />
                    </ProtectedRoute>
                }
            />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <ProgressProvider>
                <Router>
                    <AppRouter />
                </Router>
            </ProgressProvider>
        </AuthProvider>
    );
}

export default App;
